import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command.tsx';
import { LeadFilters, LeadTag } from '@/types/meta-lead.ts';
import { SlidersHorizontal, ChevronDown, Search, X, Check } from 'lucide-react';
import { getProjects } from '@/services/api/projects';

interface PageOption {
    id: string;
    name: string;
}

interface LeadFiltersProps {
    pages: PageOption[];
    tags: LeadTag[];
    filters: LeadFilters;
    onFiltersChange: (filters: LeadFilters) => void;
}

export default function LeadFiltersComponent({
    pages,
    tags,
    filters,
    onFiltersChange
}: LeadFiltersProps) {
    const [isOpen, setIsOpen] = useState(false);
    // Buffer local pra debounce — não dispara query a cada tecla
    const [searchDraft, setSearchDraft] = useState(filters.search || '');
    const [projectsPopoverOpen, setProjectsPopoverOpen] = useState(false);

    // Lista projetos que o user tem acesso (cache 5min — raramente muda)
    const { data: projects = [] } = useQuery({
        queryKey: ['projects-for-lead-filter'],
        queryFn: getProjects,
        staleTime: 1000 * 60 * 5,
    });

    const projectIds = filters.project_ids || [];
    const toggleProject = (id: number) => {
        const next = projectIds.includes(id)
            ? projectIds.filter((p) => p !== id)
            : [...projectIds, id];
        onFiltersChange({ ...filters, project_ids: next.length ? next : undefined });
    };

    // Sincroniza buffer quando filters muda externamente (ex.: clear)
    useEffect(() => {
        setSearchDraft(filters.search || '');
    }, [filters.search]);

    // Debounce 350ms — só aplica search após user parar de digitar
    useEffect(() => {
        const trimmed = searchDraft.trim();
        const current = filters.search || '';
        if (trimmed === current) return;
        const t = setTimeout(() => {
            onFiltersChange({ ...filters, search: trimmed || undefined });
        }, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchDraft]);

    return (
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50/50 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#0f172a]/5">
                        <SlidersHorizontal className="h-4 w-4 text-[#0f172a]" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">Filtros</h3>
                </div>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                <div className="overflow-hidden">
                    <div className="px-6 pb-6 pt-2 space-y-4">
                        {/* Busca por nome / sobrenome / sender_id (PSID) */}
                        <div>
                            <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                                Buscar
                            </label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                                <Input
                                    value={searchDraft}
                                    onChange={(e) => setSearchDraft(e.target.value)}
                                    placeholder="Nome, sobrenome ou ID do lead (PSID)"
                                    className="pl-9 pr-9 rounded-xl border-gray-200"
                                />
                                {searchDraft && (
                                    <button
                                        type="button"
                                        onClick={() => setSearchDraft('')}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100"
                                        aria-label="Limpar busca"
                                    >
                                        <X className="h-3.5 w-3.5 text-gray-400" />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Filtro Projeto — multi-select dropdown */}
                        <div>
                            <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                                Projeto
                            </label>
                            <Popover open={projectsPopoverOpen} onOpenChange={setProjectsPopoverOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        className="w-full justify-between font-normal rounded-xl border-gray-200"
                                    >
                                        <span className="truncate text-sm">
                                            {projectIds.length === 0
                                                ? 'Todos os projetos'
                                                : `${projectIds.length} projeto${projectIds.length > 1 ? 's' : ''} selecionado${projectIds.length > 1 ? 's' : ''}`}
                                        </span>
                                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                    className="p-0"
                                    style={{ width: 'var(--radix-popover-trigger-width)' }}
                                    align="start"
                                >
                                    <Command>
                                        <CommandInput placeholder="Buscar projeto..." />
                                        <CommandList>
                                            <CommandEmpty>Nenhum projeto encontrado.</CommandEmpty>
                                            <CommandGroup>
                                                {projects.map((p) => {
                                                    const selected = projectIds.includes(p.id);
                                                    return (
                                                        <CommandItem
                                                            key={p.id}
                                                            value={p.name}
                                                            onSelect={() => toggleProject(p.id)}
                                                        >
                                                            <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${
                                                                selected ? 'bg-violet-600 border-violet-600' : 'border-gray-300'
                                                            }`}>
                                                                {selected && <Check className="h-3 w-3 text-white" />}
                                                            </div>
                                                            <span>{p.name}</span>
                                                        </CommandItem>
                                                    );
                                                })}
                                            </CommandGroup>
                                            {projectIds.length > 0 && (
                                                <div className="border-t p-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => onFiltersChange({ ...filters, project_ids: undefined })}
                                                        className="text-xs text-gray-500 hover:underline w-full text-left px-2 py-1"
                                                    >
                                                        Limpar seleção
                                                    </button>
                                                </div>
                                            )}
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Página</label>
                                <Select
                                    value={filters.page || 'all'}
                                    onValueChange={(value) => onFiltersChange({ ...filters, page: value === 'all' ? undefined : value })}
                                >
                                    <SelectTrigger className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue placeholder="Todas" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas</SelectItem>
                                        {pages.map((page) => (
                                            <SelectItem key={page.id} value={page.id}>{page.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Tag</label>
                                <Select
                                    value={filters.tag?.toString() || 'all'}
                                    onValueChange={(value) => onFiltersChange({ ...filters, tag: value === 'all' ? undefined : parseInt(value) })}
                                >
                                    <SelectTrigger className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue placeholder="Todas" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas</SelectItem>
                                        {tags.map((tag) => (
                                            <SelectItem key={tag.id} value={tag.id.toString()}>{tag.nome}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Status</label>
                                <Select
                                    value={filters.is_active || 'all'}
                                    onValueChange={(value) => onFiltersChange({ ...filters, is_active: value as 'all' | 'yes' | 'no' })}
                                >
                                    <SelectTrigger className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos</SelectItem>
                                        <SelectItem value="yes">Ativos</SelectItem>
                                        <SelectItem value="no">Inativos (c/ erro)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Em Funil</label>
                                <Select
                                    value={filters.in_funnel || 'all'}
                                    onValueChange={(value) => onFiltersChange({ ...filters, in_funnel: value as 'all' | 'yes' | 'no' })}
                                >
                                    <SelectTrigger className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos</SelectItem>
                                        <SelectItem value="yes">Sim</SelectItem>
                                        <SelectItem value="no">Não</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Janela 24h</label>
                                <Select
                                    value={filters.in_24h_window || 'all'}
                                    onValueChange={(value) => onFiltersChange({ ...filters, in_24h_window: value as 'all' | 'yes' | 'no' })}
                                >
                                    <SelectTrigger className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos</SelectItem>
                                        <SelectItem value="yes">Aberta</SelectItem>
                                        <SelectItem value="no">Fechada</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Ordenar por</label>
                                <Select
                                    value={filters.sortBy || 'last_interaction_at'}
                                    onValueChange={(value) => onFiltersChange({ ...filters, sortBy: value as any })}
                                >
                                    <SelectTrigger className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="last_interaction_at">Última interação</SelectItem>
                                        <SelectItem value="created_at">Data de cadastro</SelectItem>
                                        <SelectItem value="nome">Nome</SelectItem>
                                        <SelectItem value="total_clicks">Clicks</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Ordem</label>
                                <Select
                                    value={filters.sortOrder || 'desc'}
                                    onValueChange={(value) => onFiltersChange({ ...filters, sortOrder: value as 'asc' | 'desc' })}
                                >
                                    <SelectTrigger className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="desc">Decrescente</SelectItem>
                                        <SelectItem value="asc">Crescente</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
